import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  ArrowLeft, 
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  FileText,
  Building2,
  MapPin,
  Briefcase
} from "lucide-react";

const SampleReportPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-[#0a2540] transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]">PromptReady</span>
            </div>
            <Badge className="bg-[#d4af37]/10 text-[#b5952f] border-[#d4af37]/30">Sample Report</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Sample Banner */}
        <div className="mb-8 p-4 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl text-center">
          <p className="text-[#0a2540] font-medium">
            This is a sample report demonstrating our AI Visibility Audit.{" "}
            <button onClick={() => navigate("/audit")} className="text-[#d4af37] underline hover:text-[#b5952f]">
              Run your own free audit →
            </button>
          </p>
        </div>

        {/* Report Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-[#d41414]/10 text-[#d41414] border border-[#d41414]/20 px-3 py-1">
              <XCircle className="w-4 h-4 mr-1" />
              Not Found
            </Badge>
            <span className="text-sm text-slate-500">Sample Report</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0a2540]">AI Visibility Audit Report</h1>
          <p className="mt-2 text-lg text-slate-600">Analysis for <span className="font-semibold">Triad Roofing Services</span></p>
        </div>

        {/* Business Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Business</p>
                <p className="font-medium text-[#0a2540]">Triad Roofing Services</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Service</p>
                <p className="font-medium text-[#0a2540]">Roofing Repair</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0a2540]/5 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#0a2540]" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Location</p>
                <p className="font-medium text-[#0a2540]">Greensboro, North Carolina</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)]">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#d4af37]" />
                  Visibility Score
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="relative w-40 h-40 mx-auto">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#dc2626" strokeWidth="8" strokeLinecap="round" strokeDasharray="51 283" transform="rotate(-90 50 50)" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-[#dc2626]">18%</span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600">URGENT: This business is invisible to AI - customers are being sent to competitors</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a2540] text-white rounded-xl border-0 shadow-lg">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Query Analyzed</p>
                <p className="font-mono text-lg text-[#d4af37]">"best roofing repair in Greensboro, North Carolina"</p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)]">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#d4af37]" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-slate-600 leading-relaxed">Triad Roofing Services is currently not being recommended by AI assistants for roofing queries in the Greensboro area. Competitors with stronger review profiles, more comprehensive content, and better local citations are dominating AI recommendations. The primary gaps are insufficient online reviews and lack of targeted service content.</p>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)]">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#d4af37]" />
                  Competitors Being Surfaced
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">These businesses appear when AI is asked about roofing in Greensboro</p>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Competitor 1 */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0a2540] flex items-center justify-center text-white text-sm font-semibold">1</div>
                      <span className="font-semibold text-[#0a2540]">Greensboro Roofing Pros</span>
                    </div>
                    <Badge className="bg-[#3ab046]/10 text-[#3ab046] border border-[#3ab046]/20">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Visible
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">Strong review presence across Google, Yelp, and HomeAdvisor with consistent 5-star ratings.</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">127 Google Reviews</Badge>
                    <Badge variant="outline" className="text-xs">BBB Accredited</Badge>
                    <Badge variant="outline" className="text-xs">Active Blog</Badge>
                  </div>
                </div>

                {/* Competitor 2 */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0a2540] flex items-center justify-center text-white text-sm font-semibold">2</div>
                      <span className="font-semibold text-[#0a2540]">Piedmont Roof Masters</span>
                    </div>
                    <Badge className="bg-[#3ab046]/10 text-[#3ab046] border border-[#3ab046]/20">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Visible
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">Comprehensive service pages with detailed FAQs answering common roofing questions.</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">Local Citations</Badge>
                    <Badge variant="outline" className="text-xs">Case Studies</Badge>
                    <Badge variant="outline" className="text-xs">Video Content</Badge>
                  </div>
                </div>

                {/* Competitor 3 */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0a2540] flex items-center justify-center text-white text-sm font-semibold">3</div>
                      <span className="font-semibold text-[#0a2540]">Carolina Storm Restoration</span>
                    </div>
                    <Badge className="bg-[#3ab046]/10 text-[#3ab046] border border-[#3ab046]/20">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Visible
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">Authoritative content on storm damage and insurance claims specific to NC homeowners.</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">NC Specialist</Badge>
                    <Badge variant="outline" className="text-xs">Insurance Guides</Badge>
                    <Badge variant="outline" className="text-xs">Emergency Service</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)]">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="text-lg font-semibold text-[#0a2540] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#d4af37]" />
                  Gap Analysis & Recommendations
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">What needs to improve to increase AI visibility</p>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Gap 1 */}
                <div className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor: '#d41414' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="priority-high">High Priority</Badge>
                    <Badge variant="outline">Content</Badge>
                  </div>
                  <p className="font-medium text-[#0a2540] mb-1">No dedicated service pages for specific roofing problems</p>
                  <p className="text-sm text-slate-600"><span className="font-medium text-[#3ab046]">Recommendation:</span> Create individual pages targeting 'roof leak repair Greensboro', 'storm damage roof repair NC', etc.</p>
                </div>

                {/* Gap 2 */}
                <div className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor: '#d41414' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="priority-high">High Priority</Badge>
                    <Badge variant="outline">Reviews</Badge>
                  </div>
                  <p className="font-medium text-[#0a2540] mb-1">Limited online reviews compared to competitors with 100+</p>
                  <p className="text-sm text-slate-600"><span className="font-medium text-[#3ab046]">Recommendation:</span> Implement systematic review collection via follow-up emails and SMS after each completed job.</p>
                </div>

                {/* Gap 3 */}
                <div className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor: '#d4af37' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="priority-medium">Medium Priority</Badge>
                    <Badge variant="outline">Citations</Badge>
                  </div>
                  <p className="font-medium text-[#0a2540] mb-1">Missing from key local directories and industry platforms</p>
                  <p className="text-sm text-slate-600"><span className="font-medium text-[#3ab046]">Recommendation:</span> Claim and optimize listings on HomeAdvisor, Angi, Thumbtack, and local Greensboro directories.</p>
                </div>

                {/* Gap 4 */}
                <div className="p-4 border-l-4 bg-white rounded-r-lg shadow-sm" style={{ borderColor: '#d4af37' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="priority-medium">Medium Priority</Badge>
                    <Badge variant="outline">Authority</Badge>
                  </div>
                  <p className="font-medium text-[#0a2540] mb-1">No educational content establishing expertise in NC roofing</p>
                  <p className="text-sm text-slate-600"><span className="font-medium text-[#3ab046]">Recommendation:</span> Publish guides on NC-specific topics: hurricane prep, ice dam prevention, HOA compliance.</p>
                </div>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-r from-[#0a2540] to-[#162536] text-white rounded-xl border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <h3 className="font-heading text-2xl font-semibold mb-2">Want to see your own AI visibility?</h3>
                <p className="text-slate-300 mb-6 max-w-md mx-auto">Run a free audit for your business and discover whether AI assistants are recommending you or your competitors.</p>
                <Button onClick={() => navigate("/audit")} className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6">
                  Run Your Free Audit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SampleReportPage;
